<script setup>
/* global axios */
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useRouter, useRoute } from 'vue-router';
import { dynamicTime } from 'shared/helpers/timeHelper';
import SegmentsAPI from 'dashboard/api/segments';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';
import { LIFECYCLE_MAP } from 'dashboard/routes/dashboard/companies/constants/companyConstants';
import ContactAttribution from 'dashboard/routes/dashboard/conversation/contact/ContactAttribution.vue';
import ContractSummary from 'dashboard/components-next/Contacts/ContractSummary.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const accountId = computed(() => route.params.accountId);
const contactId = computed(() => route.params.contactId);

// Segments
const contactSegments = ref([]);
const isFetchingSegments = ref(false);

// Form
const confirmDeleteContactDialogRef = ref(null);
const avatarFile = ref(null);
const avatarUrl = ref('');
const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const contactData = ref({});

// Collapsible sections
const showAttribution = ref(true);

// Form submissions
const formSubmissions = ref([]);
const isLoadingSubmissions = ref(false);
const expandedSubmissionId = ref(null);

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

const fetchContactSegments = async id => {
  if (!id) return;
  isFetchingSegments.value = true;
  try {
    const response = await SegmentsAPI.getContactSegments(id);
    contactSegments.value = response.data?.payload || [];
  } catch {
    contactSegments.value = [];
  } finally {
    isFetchingSegments.value = false;
  }
};

const fetchFormSubmissions = async () => {
  if (!contactId.value || !accountId.value) return;
  isLoadingSubmissions.value = true;
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/contacts/${contactId.value}/form_submissions`
    );
    formSubmissions.value = response.data?.payload || [];
  } catch {
    formSubmissions.value = [];
  } finally {
    isLoadingSubmissions.value = false;
  }
};

onMounted(() => {
  Object.assign(contactData.value, getInitialContactData());
  fetchContactSegments(props.selectedContact?.id);
  fetchFormSubmissions();
});

watch(
  () => props.selectedContact?.id,
  newId => {
    if (newId) {
      fetchContactSegments(newId);
      fetchFormSubmissions();
    }
  }
);

// Computed
const lifecycle = computed(() => {
  const stage =
    props.selectedContact?.lifecycleStage ||
    props.selectedContact?.lifecycle_stage;
  return stage ? LIFECYCLE_MAP[stage] : null;
});

const contactType = computed(() => {
  const type =
    props.selectedContact?.contact_type || props.selectedContact?.contactType;
  const map = {
    0: 'Visitante',
    1: 'Lead',
    2: 'Cliente',
    visitor: 'Visitante',
    lead: 'Lead',
    customer: 'Cliente',
  };
  return map[type] || null;
});

const createdAt = computed(() => {
  return contactData.value?.createdAt
    ? dynamicTime(contactData.value.createdAt)
    : '';
});

const lastActivityAt = computed(() => {
  return contactData.value?.lastActivityAt
    ? dynamicTime(contactData.value.lastActivityAt)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail;
});

const navigateToSegment = segmentId => {
  router.push({
    name: 'segment_detail',
    params: { accountId: store.getters.getCurrentAccountId, segmentId },
  });
};

// Form submission helpers
const submissionStatusBadge = status => {
  const map = {
    completed: {
      label: 'Completo',
      class:
        'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400',
    },
    partial: {
      label: 'Parcial',
      class:
        'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
    },
    superseded: {
      label: 'Substituido',
      class:
        'bg-n-slate-3 text-n-slate-11',
    },
  };
  return map[status] || map.completed;
};

const formatSubmissionDate = dateStr => {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const previewSubmissionData = data => {
  if (!data || typeof data !== 'object') return '--';
  const entries = Object.entries(data).filter(
    ([, v]) => v && v.toString().trim() !== ''
  );
  if (entries.length === 0) return '--';
  return entries
    .slice(0, 3)
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ');
};

const toggleSubmission = id => {
  expandedSubmissionId.value = expandedSubmissionId.value === id ? null : id;
};

// Contact actions
const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const updateContact = async () => {
  try {
    const { customAttributes, ...basicContactData } = contactData.value;
    await store.dispatch('contacts/update', basicContactData);
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;
  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact?.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="max-w-5xl mx-auto space-y-5">
    <!-- ===== SECTION: Avatar + Quick Info ===== -->
    <div class="rounded-xl border border-n-slate-3 bg-n-solid-2 p-5">
      <div class="flex items-start gap-4">
        <Avatar
          :src="avatarSrc || ''"
          :name="selectedContact?.name || ''"
          :size="64"
          allow-upload
          @upload="handleAvatarUpload"
          @delete="handleAvatarDelete"
        />
        <div class="flex flex-col gap-1.5 min-w-0 flex-1">
          <h3 class="text-base font-semibold text-n-slate-12 truncate">
            {{ selectedContact?.name }}
          </h3>
          <div
            class="flex items-center gap-3 text-xs text-n-slate-10 flex-wrap"
          >
            <span
              v-if="selectedContact?.email"
              class="inline-flex items-center gap-1"
            >
              <span class="i-lucide-mail size-3" />
              {{ selectedContact.email }}
            </span>
            <span
              v-if="selectedContact?.phoneNumber"
              class="inline-flex items-center gap-1"
            >
              <span class="i-lucide-phone size-3" />
              {{ selectedContact.phoneNumber }}
            </span>
            <span
              v-if="selectedContact?.identifier"
              class="inline-flex items-center gap-1"
            >
              <span class="i-lucide-hash size-3" />
              {{ selectedContact.identifier }}
            </span>
          </div>
          <div class="flex items-center gap-2 text-[11px] text-n-slate-9">
            <span>
              {{
                $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt })
              }}
            </span>
            <span>·</span>
            <span>
              {{
                $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                  date: lastActivityAt,
                })
              }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== SECTION: Contract Summary ===== -->
    <ContractSummary
      v-if="selectedContact?.id"
      entity-type="contact"
      :entity-id="selectedContact.id"
    />

    <!-- ===== SECTION: Labels + Segments ===== -->
    <div class="rounded-xl border border-n-slate-3 bg-n-solid-2 p-5">
      <h3
        class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider mb-4"
      >
        Labels e Segmentos
      </h3>
      <div class="flex flex-col gap-3">
        <ContactLabels :contact-id="selectedContact?.id" />
        <div
          v-if="contactSegments.length > 0"
          class="flex flex-wrap items-center gap-1.5"
        >
          <span class="text-[10px] text-n-slate-9 uppercase tracking-wide mr-1">
            Segmentos:
          </span>
          <button
            v-for="segment in contactSegments"
            :key="segment.id"
            class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-400 border border-violet-200 dark:border-violet-800 hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors cursor-pointer"
            @click="navigateToSegment(segment.id)"
          >
            <span class="i-lucide-pie-chart size-3" />
            {{ segment.name }}
          </button>
        </div>
      </div>
    </div>

    <!-- ===== SECTION: Dados do Contato (editable form) ===== -->
    <div class="rounded-xl border border-n-slate-3 bg-n-solid-2 p-5">
      <h3
        class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider mb-4"
      >
        Dados do contato
      </h3>
      <ContactsForm
        ref="contactsFormRef"
        :contact-data="contactData"
        is-details-view
        @update="handleFormUpdate"
      />
    </div>

    <!-- ===== SECTION: Lifecycle ===== -->
    <div class="rounded-xl border border-n-slate-3 bg-n-solid-2 p-5">
      <h3
        class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider mb-4"
      >
        Lifecycle
      </h3>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4"
      >
        <div>
          <span class="text-xs text-n-slate-9">Estagio</span>
          <p class="mt-0.5 flex items-center gap-1.5 !mb-0">
            <span
              v-if="lifecycle"
              class="text-xs font-semibold px-2.5 py-1 rounded-md"
              :class="lifecycle.bgClass"
            >
              {{ lifecycle.label }}
            </span>
            <span v-else class="text-sm font-medium text-n-slate-12">
              Nao definido
            </span>
            <span class="text-[10px] text-n-slate-8 italic">
              auto (deals)
            </span>
          </p>
        </div>
        <div>
          <span class="text-xs text-n-slate-9">Tipo de contato</span>
          <p class="text-sm font-medium text-n-slate-12 mt-0.5 !mb-0">
            {{ contactType || '-' }}
          </p>
        </div>
        <div>
          <span class="text-xs text-n-slate-9">Criado em</span>
          <p class="text-sm font-medium text-n-slate-12 mt-0.5 !mb-0">
            {{ createdAt || '-' }}
          </p>
        </div>
        <div>
          <span class="text-xs text-n-slate-9">Ultima atividade</span>
          <p class="text-sm font-medium text-n-slate-12 mt-0.5 !mb-0">
            {{ lastActivityAt || '-' }}
          </p>
        </div>
      </div>
    </div>

    <!-- ===== SECTION: Atribuição / UTM (read-only, collapsible) ===== -->
    <div
      class="rounded-xl border border-n-slate-3 bg-n-solid-2 overflow-hidden"
    >
      <button
        class="flex items-center justify-between w-full px-5 py-3 text-left"
        @click="showAttribution = !showAttribution"
      >
        <span
          class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider"
        >
          Atribuição / UTM
        </span>
        <span
          class="size-4 text-n-slate-9 transition-transform"
          :class="
            showAttribution ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
          "
        />
      </button>
      <div v-if="showAttribution" class="px-5 pb-5">
        <ContactAttribution :contact="selectedContact" />
      </div>
    </div>

    <!-- ===== SECTION: Formularios ===== -->
    <div class="rounded-xl border border-n-slate-3 bg-n-solid-2 p-5">
      <h3
        class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider mb-4"
      >
        Formularios
      </h3>

      <!-- Loading -->
      <div
        v-if="isLoadingSubmissions"
        class="flex items-center justify-center py-6"
      >
        <div
          class="size-6 border-2 border-n-iris-9 border-t-transparent rounded-full animate-spin"
        />
      </div>

      <!-- Empty -->
      <div
        v-else-if="formSubmissions.length === 0"
        class="flex flex-col items-center py-6 gap-1.5"
      >
        <span class="i-lucide-file-text size-6 text-n-slate-9" />
        <p class="text-sm text-n-slate-10 !mb-0">
          Nenhuma submissão de formulário.
        </p>
      </div>

      <!-- List -->
      <div v-else class="flex flex-col gap-2">
        <div
          v-for="sub in formSubmissions"
          :key="sub.id"
          class="border border-n-weak rounded-lg overflow-hidden"
        >
          <button
            class="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-n-alpha-1 transition-colors"
            @click="toggleSubmission(sub.id)"
          >
            <span
              class="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
              :class="submissionStatusBadge(sub.status).class"
            >
              {{ submissionStatusBadge(sub.status).label }}
            </span>
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ sub.form_name || 'Formulario #' + sub.form_id }}
            </span>
            <span
              class="text-xs text-n-slate-9 truncate hidden sm:block flex-1"
            >
              {{ previewSubmissionData(sub.data) }}
            </span>
            <span class="text-xs text-n-slate-9 flex-shrink-0">
              {{ formatSubmissionDate(sub.created_at) }}
            </span>
            <span
              class="i-lucide-chevron-down size-4 text-n-slate-9 transition-transform flex-shrink-0"
              :class="expandedSubmissionId === sub.id ? 'rotate-180' : ''"
            />
          </button>
          <div
            v-if="expandedSubmissionId === sub.id"
            class="px-4 py-3 bg-n-alpha-black2 border-t border-n-weak"
          >
            <div class="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <div
                v-for="(value, key) in sub.data || {}"
                :key="key"
                class="bg-n-solid-2 rounded-lg p-3 border border-n-weak"
              >
                <p
                  class="text-[10px] font-medium text-n-slate-9 uppercase tracking-wider !mb-0.5"
                >
                  {{ key }}
                </p>
                <p class="text-sm text-n-slate-12 !mb-0 break-words">
                  {{ value || '--' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== SECTION: Atributos personalizados ===== -->
    <div
      class="rounded-xl border border-n-slate-3 bg-n-solid-2 overflow-hidden [&>div]:!px-5 [&>p]:!px-5"
    >
      <div class="px-5 pt-4 pb-0">
        <span
          class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider"
        >
          Atributos personalizados
        </span>
      </div>
      <ContactCustomAttributes :selected-contact="selectedContact" />
    </div>

    <!-- ===== SAVE BUTTON ===== -->
    <div class="flex items-center gap-3">
      <Button
        :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="updateContact"
      />
    </div>

    <!-- ===== DANGER ZONE ===== -->
    <Policy :permissions="['administrator']">
      <div
        class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
      >
        <div class="flex flex-col gap-2">
          <h6 class="text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </h6>
          <span class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
          </span>
        </div>
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')"
          color="ruby"
          @click="openConfirmDeleteContactDialog"
        />
      </div>
      <ConfirmContactDeleteDialog
        ref="confirmDeleteContactDialogRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')"
      />
    </Policy>
  </div>
</template>
